import React, { useEffect, useRef, useState } from "react";
import {
    X as XIcon, Video, AlertTriangle, Play, RefreshCw,
} from "lucide-react";
import { fetchScenarioVideo, fetchScenarioMeta } from "../../services/LabService";

/**
 * ScenarioVideoModal — the audit drilldown.
 *
 * Shows a 13-second video (5s before t0, 8s after) of the scene the model
 * was evaluated on, with a marker line at t0 on the timeline. The customer
 * watches the actual driving moment to understand why the model failed
 * (or succeeded). One camera at a time, switchable via the camera selector.
 *
 * Props:
 *   scenarioId   — the scenario UUID (e.g. "0a11e026-a838-...")
 *   notes        — optional list of consistency-failure notes to show
 *                  alongside the video (the audit's "what went wrong")
 *   onClose      — modal dismiss handler
 *
 * Failure modes:
 *   - Video not available (older scenarios, --include-video flag not used):
 *     show a clear "video not available" notice with fallback to still frames.
 *   - Network failure: retry button.
 *   - Presigned URL expired (5 min): auto-refresh.
 */
const CAMERAS = [
    { key: "front_wide", label: "Front wide" },
    { key: "front_tele", label: "Front tele (zoom)" },
    { key: "cross_left", label: "Cross left" },
    { key: "cross_right", label: "Cross right" },
];

const PRE_SECONDS = 5;    // matches the import script's pre/post window
const POST_SECONDS = 8;
const T0_FRACTION = PRE_SECONDS / (PRE_SECONDS + POST_SECONDS);

const ScenarioVideoModal = ({ scenarioId, notes, onClose }) => {
    const [camera, setCamera] = useState("front_wide");
    const [videoUrl, setVideoUrl] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loadState, setLoadState] = useState("loading"); // loading | ready | unavailable | error
    const [errorMsg, setErrorMsg] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!scenarioId) return;
            try {
                const m = await fetchScenarioMeta(scenarioId);
                if (!cancelled) setMeta(m);
            } catch (e) { /* meta is optional — keep going */ }
        })();
        return () => { cancelled = true; };
    }, [scenarioId]);

    useEffect(() => {
        let cancelled = false;
        setLoadState("loading");
        setErrorMsg(null);
        setVideoUrl(null);
        (async () => {
            try {
                const resp = await fetchScenarioVideo(scenarioId, camera);
                if (cancelled) return;
                setVideoUrl(resp.video_url);
                setLoadState("ready");
            } catch (e) {
                if (cancelled) return;
                if (e.status === 404) {
                    setLoadState("unavailable");
                } else {
                    setLoadState("error");
                    setErrorMsg(e.message);
                }
            }
        })();
        return () => { cancelled = true; };
    }, [scenarioId, camera]);

    // ESC closes
    useEffect(() => {
        const handler = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    // Auto-jump to the t0 moment so the user lands at the decision point
    const onVideoLoaded = () => {
        if (videoRef.current && videoRef.current.duration) {
            videoRef.current.currentTime = videoRef.current.duration * T0_FRACTION;
        }
    };

    const retry = () => {
        setLoadState("loading");
        setVideoUrl(null);
        fetchScenarioVideo(scenarioId, camera)
            .then((r) => { setVideoUrl(r.video_url); setLoadState("ready"); })
            .catch((e) => {
                setLoadState(e.status === 404 ? "unavailable" : "error");
                setErrorMsg(e.message);
            });
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div className="bg-white dark:bg-slate-900 rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
                    <div className="flex items-center gap-2">
                        <Video className="w-4 h-4 text-indigo-500" />
                        <h3 className="text-sm font-semibold">Scenario playback</h3>
                        <code className="text-xs text-slate-500 ml-2">{scenarioId}</code>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                        aria-label="Close"
                    >
                        <XIcon className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-5">
                    {/* Camera selector */}
                    <div className="flex items-center gap-1 mb-3 overflow-x-auto">
                        {CAMERAS.map((c) => (
                            <button
                                key={c.key}
                                onClick={() => setCamera(c.key)}
                                className={`px-3 py-1.5 text-xs rounded-md border transition flex-shrink-0 ${
                                    camera === c.key
                                        ? "bg-indigo-600 text-white border-indigo-600"
                                        : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700"
                                }`}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {/* Video player */}
                    <div className="relative bg-slate-950 rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }}>
                        {loadState === "loading" ? (
                            <div className="absolute inset-0 flex items-center justify-center text-slate-400 text-sm">
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Loading video…
                            </div>
                        ) : loadState === "unavailable" ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 text-sm gap-2 p-6 text-center">
                                <AlertTriangle className="w-8 h-8 text-amber-400" />
                                <div className="font-medium">Video not available for this scenario.</div>
                                <div className="text-xs text-slate-400 max-w-md">
                                    This clip was imported before video extraction was enabled.
                                    Re-run the importer with <code className="bg-slate-800 px-1.5 py-0.5 rounded">--include-video</code> to
                                    generate playback for this scenario.
                                </div>
                            </div>
                        ) : loadState === "error" ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 text-sm gap-3 p-6 text-center">
                                <AlertTriangle className="w-8 h-8 text-red-400" />
                                <div>Could not load video: {errorMsg}</div>
                                <button onClick={retry} className="text-xs px-3 py-1.5 bg-indigo-600 text-white rounded-md">Retry</button>
                            </div>
                        ) : (
                            <video
                                ref={videoRef}
                                src={videoUrl}
                                controls
                                autoPlay
                                loop
                                onLoadedMetadata={onVideoLoaded}
                                className="w-full h-full"
                            />
                        )}
                    </div>

                    {/* t0 marker explanation */}
                    {loadState === "ready" && (
                        <div className="mt-2 text-xs text-slate-500 flex items-center justify-between">
                            <div>
                                <span className="inline-block w-2 h-2 bg-indigo-500 rounded-full mr-1.5"></span>
                                Auto-seeked to <b>t = {PRE_SECONDS}s</b> (model's decision moment)
                            </div>
                            <div>
                                Window: −{PRE_SECONDS}s … +{POST_SECONDS}s around t0
                            </div>
                        </div>
                    )}

                    {/* Audit notes — what went wrong */}
                    {notes && notes.length > 0 ? (
                        <div className="mt-4 border border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20 rounded-md p-3">
                            <div className="text-xs font-semibold flex items-center gap-1.5 mb-2 text-amber-700 dark:text-amber-300">
                                <AlertTriangle className="w-3.5 h-3.5" /> Why this scenario surfaced
                            </div>
                            <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-1">
                                {notes.map((n, i) => <li key={i}>• {n}</li>)}
                            </ul>
                        </div>
                    ) : null}

                    {/* Meta sidebar */}
                    {meta && (
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                            {meta.command ? (
                                <div>
                                    <div className="text-slate-500 mb-0.5">Command</div>
                                    <div className="font-medium">{meta.command}</div>
                                </div>
                            ) : null}
                            {meta.t0_us != null ? (
                                <div>
                                    <div className="text-slate-500 mb-0.5">t0 in clip</div>
                                    <div className="font-mono">{(meta.t0_us / 1e6).toFixed(2)}s</div>
                                </div>
                            ) : null}
                            {meta.ground_truth_trajectory && meta.ground_truth_trajectory.length > 0 ? (
                                <div>
                                    <div className="text-slate-500 mb-0.5">GT endpoint</div>
                                    <div className="font-mono">
                                        ({meta.ground_truth_trajectory[meta.ground_truth_trajectory.length - 1][0].toFixed(1)}m,&nbsp;
                                         {meta.ground_truth_trajectory[meta.ground_truth_trajectory.length - 1][1].toFixed(1)}m)
                                    </div>
                                </div>
                            ) : null}
                            {meta.source ? (
                                <div>
                                    <div className="text-slate-500 mb-0.5">Source</div>
                                    <div className="font-medium">{meta.source}</div>
                                </div>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScenarioVideoModal;
